import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { initSync, teardownSync } from '@/lib/sync-engine';
import { migrateLocalData } from '@/lib/data-migration';
import { isSupabaseConfigured } from '@/lib/supabase';

const SyncContext = createContext(null);

export function SyncProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [synced, setSynced] = useState(false);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user || !isSupabaseConfigured()) {
      setSynced(false);
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      try {
        // Check if user needs localStorage migration
        if (!user.migratedFromLocal) {
          setMigrating(true);
          await migrateLocalData(user.id);
          setMigrating(false);
        }

        await initSync();
        if (!cancelled) setSynced(true);
      } catch (err) {
        console.error('Sync initialization failed:', err);
        if (!cancelled) {
          setMigrating(false);
          setSynced(false);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      teardownSync();
      setSynced(false);
    };
  }, [isAuthenticated, user?.id]);

  return (
    <SyncContext.Provider value={{ synced, migrating }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  return useContext(SyncContext);
}
